import {
  DISPATCH_MAP_FILTERS,
  DEFAULT_DISPATCH_MAP_FILTERS,
} from "./constants";
import appConstant from "./../../../../appConstants";
import { isAfter,isSameDay } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { getDiffInMinutesWithTerminalTime, filterServiceLevelsByMode } from "../utils/utils";
import { getServiceLevelValues } from "../../shared/serviceLevelUtils";

const { SERVICE_LEVEL_MODE_FILTERS } = appConstant;

const isPickupFiltered = ({
  pickup,
  filters,
  terminalOffset,
  duplicatePickups=[],
}) => {
  const isMatch = filters.map((pickupFilter) => {
    return filterPickup({
      pickup,
      pickupFilter,
      terminalOffset,
      duplicatePickups,
  });
  });
  return Boolean(isMatch.filter(Boolean).length);
};

const filterPickup = ({
  pickup,
  pickupFilter,
  terminalOffset,
  duplicatePickups,
}) => {
  return pickupFilter?.filterFunc?.({
    pickup,
    terminalOffset,
    duplicatePickups,
  });
};

const isMissedPickup = ({pickup}) => pickup.isMissed && !pickup.isVoided;

const checkIsReturnDeliveryStop=(shipments)=>{
    return shipments.some(shipment=>shipment.returnCode);
}

const isStopFiltered = (stop, filters, serviceLevelList) => {
    const isReturnStop=checkIsReturnDeliveryStop(stop.shipments)
    const pickupPriorityList = filterServiceLevelsByMode(serviceLevelList, SERVICE_LEVEL_MODE_FILTERS.PICKUP_MODES);
    const deliveryPriorityList = filterServiceLevelsByMode(serviceLevelList, SERVICE_LEVEL_MODE_FILTERS.DELIVERY_MODES);
    const isMatch = filters.map(stopFilter => {
      switch (stopFilter) {
          case DISPATCH_MAP_FILTERS.deliveryCompleted:  
              return Boolean(stop.stopType === appConstant.STOP_TYPE.Delivery && stop.isStopCompleted && !isReturnStop)
          case DISPATCH_MAP_FILTERS.pickupCompleted:  
              return Boolean(stop.stopType === appConstant.STOP_TYPE.Pickup && stop.isStopCompleted)
          case DISPATCH_MAP_FILTERS.pickupPending:
              return Boolean(stop.stopType === appConstant.STOP_TYPE.Pickup && !stop.isStopCompleted)
          case DISPATCH_MAP_FILTERS.pickupPriority:  
              return Boolean(stop.stopType === appConstant.STOP_TYPE.Pickup && hasPriority(stop.priority, pickupPriorityList))
          case DISPATCH_MAP_FILTERS.deliveryReturned:  
              return Boolean(stop.stopType === appConstant.STOP_TYPE.Delivery && stop.isStopCompleted && isReturnStop )
          case DISPATCH_MAP_FILTERS.deliveryPending:  
              return Boolean(stop.stopType === appConstant.STOP_TYPE.Delivery && !stop.isStopCompleted)
          case DISPATCH_MAP_FILTERS.deliveryPriority:  
              return Boolean(stop.stopType === appConstant.STOP_TYPE.Delivery && hasPriority(stop.priority, deliveryPriorityList))
          default:
              break;
      }
     })
  return Boolean(isMatch.filter(Boolean).length)
}

/* priorityList holds mapped service levels as stored on the shipment, so serviceLevelList is
 normalized to its mapped values before matching. */
const hasPriority = (priorityList, serviceLevelList) => {
  const serviceLevelValues = getServiceLevelValues(serviceLevelList);
  return priorityList?.some((priority) => serviceLevelValues.includes(priority));
};

const getSelectedMapFilters=(selectedFiltersInStore)=>{
    return selectedFiltersInStore?.length?
        selectedFiltersInStore
        : DEFAULT_DISPATCH_MAP_FILTERS;
}

const isFuturePickup = (pickupDate, terminalOffset) => {
  return isAfter(
    new Date(pickupDate + "T00:00:00"),
    utcToZonedTime(new Date(), terminalOffset)
  );
  /* If you do not pass anything with date string into the new date constructor, it will give you a date object of whatever the date and current time is,
   since the local system time is used to interpret the date, depending upon where system is,we could get different results from the 
   same non-specific date. So, here we use a full date string with hours:minutes.milliseconds to resolve the issue. 
   example
    1) new Date(22-08-16) = Tue Aug 16 2022 05:30:00 GMT+0530 (India Standard Time) | Mon Aug 15 2022 20:00:00 GMT-0400 (Eastern Daylight Time)
    2) new Date('2022-08-16T00:00:00') = Tue Aug 16 2022 00:00:00 GMT+0530 (India Standard Time) | Tue Aug 16 2022 00:00:00 GMT-0400 (Eastern Daylight Time)
  */
};

const isNotCompletedPickup = ({pickup, terminalOffset}) => {
  return (
    Boolean(pickup.driverId) &&
    !pickup.isCompleted &&
    !pickup.isVoided &&
    isTodaysPickup(pickup.pickupDate, terminalOffset)
  );
};

const isNotAssignedPickup = ({pickup, terminalOffset}) => {
  return (
    !Boolean(pickup.driverId) &&
    !pickup.isCompleted &&
    !pickup.isVoided &&
    isTodaysPickup(pickup.pickupDate, terminalOffset)
  );
};

const isUnAcknowledgedPickup = ({pickup, terminalOffset}) => {
  return (
    !pickup.isAcknowledged &&
    Boolean(pickup.driverId) &&
    !pickup.isVoided && !pickup.isCompleted &&
    isTodaysPickup(pickup.pickupDate, terminalOffset)
  );
};

const isCompletedPickup = ({pickup, terminalOffset}) => {
  return (
    pickup.isCompleted && isTodaysPickup(pickup.pickupDate, terminalOffset)
  );
};

const isNoFreightPickup = ({pickup}) => {
  return (
    pickup.isNoFreight
  );
};

const isToBeReviewedPickup = ({pickup}) => {
  return (
    pickup.isToBeReviewed && !pickup.isVoided && !pickup.isMissed && !pickup.isNoFreight
  );
};

const isNewPickup = ({pickup, terminalOffset, newPickupDuration}) => {
  if (
    Boolean(pickup.driverId) ||
    !isSameDay(
      new Date(pickup.createdDateTime),
      utcToZonedTime(new Date(), terminalOffset)
    )
  ) {
    return;
  }
  const timeDiff = getDiffInMinutesWithTerminalTime(
    pickup.createdDateTime,
    terminalOffset
  );
  return timeDiff <= newPickupDuration;
  /* A pickup is a new pickup if it has been created in not more than 10 minutes. */
};

const isTodaysPickup = (pickupDate, terminalOffset) => {
  return isSameDay(
    new Date(pickupDate + "T00:00:00"),
    utcToZonedTime(new Date(), terminalOffset)
  );
  /* If you do not pass anything with date string into the new date constructor, it will give you a date object of whatever the date and current time is,
   since the local system time is used to interpret the date, depending upon where system is,we could get different results from the 
   same non-specific date. So, here we use a full date string with hours:minutes.milliseconds to resolve the issue. 
   example
    1) new Date(22-08-16) = Tue Aug 16 2022 05:30:00 GMT+0530 (India Standard Time) | Mon Aug 15 2022 20:00:00 GMT-0400 (Eastern Daylight Time)
    2) new Date('2022-08-16T00:00:00') = Tue Aug 16 2022 00:00:00 GMT+0530 (India Standard Time) | Tue Aug 16 2022 00:00:00 GMT-0400 (Eastern Daylight Time)
  */
};

const isUnprocessedPickup = ({pickup}) => {
  return (
    !isToBeReviewedPickup({pickup}) && pickup.prevAssignedDriver
  );
};

export {
  isPickupFiltered,
  isStopFiltered,
  getSelectedMapFilters,
  isFuturePickup,
  isNotCompletedPickup,
  isNotAssignedPickup,
  isUnAcknowledgedPickup,
  isCompletedPickup,
  hasPriority,
  isTodaysPickup,
  filterPickup,
  isMissedPickup,
  isNewPickup,
  isNoFreightPickup,
  isToBeReviewedPickup,
  isUnprocessedPickup
};
